import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Box, Typography } from "../ui";
import {
  clearRecordRefused,
  refusedRecordPage,
  subscribeRecordRefused,
} from "../utils/recordAccess";

/** The message, one sentence to a line. */
export const NO_ACCESS_LINES = [
  "You currently do not have access to this Company's information.",
  "Please contact your system administrator for more information.",
];

/** The message, in a wide light-gray pill centred where the page would be. */
export function NoRecordAccess() {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "50vh",
        px: 2,
      }}
    >
      <Box
        role="alert"
        sx={{
          width: "100%",
          maxWidth: 880,
          bgcolor: "grey.200",
          color: "text.primary",
          // A pill on a wide screen; on a phone the text wraps to several
          // lines, and a full radius would pinch them into a lozenge.
          borderRadius: { xs: "24px", sm: "9999px" },
          px: { xs: 3, sm: 6 },
          py: 3,
          textAlign: "center",
        }}
      >
        {NO_ACCESS_LINES.map((line) => (
          <Typography key={line} variant="body1" sx={{ fontWeight: 500 }}>
            {line}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

/**
 * The routed page, or the message when the router refused the record this
 * page is for (organization-scoping 3.6c). Without it the page renders empty:
 * every screen treats a rejected load as a record with no fields.
 *
 * The refusal is tied to the pathname it came from, so a late answer for a
 * page already left never covers the next one, and every navigation clears
 * it - the next page asks the router again.
 */
export function RecordAccessGate({ children }) {
  const location = useLocation();
  const [refused, setRefused] = useState(refusedRecordPage);

  useEffect(() => subscribeRecordRefused(setRefused), []);
  useEffect(() => {
    clearRecordRefused();
  }, [location.key]);

  if (refused && refused === location.pathname) return <NoRecordAccess />;
  return children;
}

export default RecordAccessGate;
